import { useState, useCallback, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { getConversations, deleteConversation } from '../services/chatService';

/**
 * Hook for managing conversations list
 */
export const useConversations = (autoLoad = false) => {
  const { user } = useUser();
  const userId = user?.id;

  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all conversations for the user
   */
  const fetchConversations = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getConversations(userId);
      const conversationsList = response.data || response.conversations || response;
      setConversations(Array.isArray(conversationsList) ? conversationsList : []);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversations. Please try again.');
      setConversations([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  /**
   * Delete a conversation by ID
   */
  const removeConversation = useCallback(
    async (conversationId) => {
      setError(null);

      try {
        await deleteConversation(conversationId);

        // Update local state
        setConversations((prev) => prev.filter((conv) => conv.id !== conversationId && conv._id !== conversationId));

        return true;
      } catch (err) {
        console.error('Failed to delete conversation:', err);
        setError('Failed to delete conversation. Please try again.');
        return false;
      }
    },
    []
  );

  /**
   * Refresh conversations list
   */
  const refreshConversations = useCallback(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Auto-load conversations on mount if enabled
  useEffect(() => {
    if (autoLoad && userId) {
      fetchConversations();
    }
  }, [autoLoad, userId, fetchConversations]);

  return {
    conversations,
    isLoading,
    error,
    fetchConversations,
    removeConversation,
    refreshConversations,
  };
};
